// Forecast Lock & 4-Week Plan — a hub manager's planning console.
//
// Shows a colorful 4-week forward view (volume by week / hub / customer / branch,
// package-type mix, LH inbound, hourly volume availability, capacity highlights)
// AND lets an admin freeze the first week as a committed plan (the "lock"), with
// live-vs-locked drift so you can see when the plan needs refreshing.
//
// Process for CHANGING a locked plan: you never edit the frozen numbers directly.
// Adjust the inputs (customers, calibration, events, overrides) as normal — the
// live forecast moves and the drift grows — then hit "Re-lock" (with a note) to
// commit the new reality. The old snapshot is kept as history. "Unlock" abandons
// the commitment entirely. Lock/unlock is admin-only.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Input, PageHeader, Select, Spinner } from "../../components/ui";
import { BarsChart, Donut, HBarList, HourBars, StackedBars } from "../../components/charts";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Branch, Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import { forecastCustomerHub } from "../../lib/forecast/engine";
import { hourlyAvailability, lhLoadsInOnDay } from "../../lib/forecast/arrivals";
import { addPackageSplit, emptyPackageSplit, splitPackageMix } from "../../lib/forecast/hubMetrics";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { buildLockCells, lockedHubWeekTotals, weekDates, type LockCell, type LockFlow } from "../../lib/forecast/lockSnapshot";
import {
  createLock,
  getActiveLockForWeek,
  getLockCells,
  listActiveLocks,
  unlockWeek,
  type ForecastLock,
} from "./forecastLockApi";
import { getFlowShareTotals, type BranchFlow } from "../actuals/branchFlowApi";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listBranches } from "../branches/branchesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";

const ALL = "__all__";
const WEEK_OPTIONS = 9;
const TYPE_COLORS = { cartons: "#0f766e", polybags: "#0ea5e9", irregular: "#f59e0b", unspec: "#cbd5e1" } as const;
const WEEK_COLORS = ["#0f766e", "#0ea5e9", "#a855f7", "#f59e0b"];
const HUB_PALETTE = ["#0f766e", "#0ea5e9", "#a855f7", "#f59e0b", "#ec4899", "#10b981", "#6366f1", "#ef4444", "#14b8a6", "#f97316"];

type Split = { cartons: number; polybags: number; irregular: number; unspec: number };

export default function ForecastLockPage() {
  const { activeAccount, session } = useAuth();
  const accountId = activeAccount?.accountId ?? null;
  const isAdmin = activeAccount?.role === "admin";

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [liveFlows, setLiveFlows] = useState<BranchFlow[]>([]);
  const [locks, setLocks] = useState<ForecastLock[]>([]);

  const [hubId, setHubId] = useState(ALL);
  const [startWeek, setStartWeek] = useState(() => startOfWeekSat(addDays(today(), 7)));
  const [topN, setTopN] = useState(8);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  const [lock, setLock] = useState<ForecastLock | null>(null);
  const [lockCells, setLockCells] = useState<LockCell[]>([]);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listBranches(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountLhSchedule(accountId),
      listActiveLocks(accountId),
    ])
      .then(([c, h, b, ln, ov, ev, lh, lk]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setLhRows(lh);
        setLocks(lk);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load planning data.")))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    getFlowShareTotals(accountId, addDays(startWeek, -28), addDays(startWeek, -1))
      .then(setLiveFlows)
      .catch(() => setLiveFlows([]));
  }, [accountId, startWeek]);

  const refreshLock = useCallback(async () => {
    if (!accountId) return;
    const lk = await getActiveLockForWeek(accountId, startWeek);
    setLock(lk);
    setLockCells(lk ? await getLockCells(lk.id) : []);
  }, [accountId, startWeek]);

  useEffect(() => {
    refreshLock().catch((e) => setError(getErrorMessage(e, "Failed to load the lock.")));
  }, [refreshLock]);

  const isAll = hubId === ALL;
  const hubSet = useMemo(() => (isAll ? new Set(hubs.map((h) => h.id)) : new Set([hubId])), [isAll, hubs, hubId]);
  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const laneMix = useMemo(() => {
    const m = new Map<string, { cartons: number; polybags: number; irregular: number }>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.packageMix);
    return m;
  }, [lanes]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);

  const weeks4 = useMemo(
    () =>
      Array.from({ length: 4 }, (_, k) => {
        const ws = addDays(startWeek, k * 7);
        return { weekStart: ws, weekEnd: addDays(ws, 6), dates: weekDates(ws) };
      }),
    [startWeek]
  );
  const allDates = useMemo(() => weeks4.flatMap((w) => w.dates), [weeks4]);

  const cFcst = useMemo(() => {
    const cache = new Map<string, number>();
    return (c: Customer, h: string, d: string) => {
      const k = `${c.id}|${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastCustomerHub(c, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [overrideMap, events]);

  // ── One pass: per-hub-day own volume (all hubs) + filtered customer/package aggregates ──
  const plan = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    const hubDayOwn = new Map<string, number>(); // `${hub}|${date}` (all hubs)
    const hubWeekAll = new Map<string, number[]>(); // hub → [4] (all hubs)
    const custTotal = new Map<string, number>();
    const custName = new Map<string, string>();
    const custHub = new Map<string, number>(); // `${cust}|${hub}` (filtered)
    const pkgWeek: Split[] = Array.from({ length: 4 }, () => emptyPackageSplit());
    const dayPkg = new Map<string, Split>(); // filtered package split per date (for MHE peak)
    let pkgAll: Split = emptyPackageSplit();

    for (const h of allHubIds) {
      const wk = [0, 0, 0, 0];
      weeks4.forEach((w, wi) => {
        for (const d of w.dates) {
          let own = 0;
          for (const c of customers) {
            const v = cFcst(c, h, d);
            if (v <= 0) continue;
            own += v;
            if (hubSet.has(h)) {
              custTotal.set(c.id, (custTotal.get(c.id) ?? 0) + v);
              custName.set(c.id, c.name);
              custHub.set(`${c.id}|${h}`, (custHub.get(`${c.id}|${h}`) ?? 0) + v);
              const sp = splitPackageMix(v, laneMix.get(`${c.id}|${h}`));
              pkgWeek[wi] = addPackageSplit(pkgWeek[wi], sp);
              pkgAll = addPackageSplit(pkgAll, sp);
              dayPkg.set(d, addPackageSplit(dayPkg.get(d) ?? emptyPackageSplit(), sp));
            }
          }
          hubDayOwn.set(`${h}|${d}`, own);
          wk[wi] += own;
        }
      });
      hubWeekAll.set(h, wk);
    }

    // Filtered weekly + daily totals.
    const weekTot = [0, 0, 0, 0];
    const dayTot: Array<{ date: string; weekIdx: number; value: number }> = [];
    weeks4.forEach((w, wi) => {
      for (const d of w.dates) {
        let v = 0;
        for (const h of hubSet) v += hubDayOwn.get(`${h}|${d}`) ?? 0;
        weekTot[wi] += v;
        dayTot.push({ date: d, weekIdx: wi, value: v });
      }
    });

    return { hubDayOwn, hubWeekAll, custTotal, custName, custHub, pkgWeek, pkgAll, dayPkg, weekTot, dayTot };
  }, [hubs, customers, weeks4, hubSet, cFcst, laneMix]);

  const total4wk = plan.weekTot.reduce((s, v) => s + v, 0);
  const peakWeekIdx = plan.weekTot.indexOf(Math.max(...plan.weekTot));
  const peakDay = plan.dayTot.reduce((a, b) => (b.value > a.value ? b : a), { date: "", weekIdx: 0, value: 0 });

  // Hub own 4-week totals (all hubs) — drives by-hub + branch.
  const hubOwn4 = useMemo(() => {
    const m = new Map<string, number>();
    for (const [h, wk] of plan.hubWeekAll) m.set(h, wk.reduce((s, v) => s + v, 0));
    return m;
  }, [plan.hubWeekAll]);

  const shares = useMemo(() => flowShares(liveFlows), [liveFlows]);
  const branchTotals = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    return branches
      .map((b) => {
        let v = 0;
        for (const o of allHubIds) v += (hubOwn4.get(o) ?? 0) * shareOf(shares, o, b.id);
        return { b, value: v };
      })
      .filter((r) => r.value > 0 && (isAll || hubSet.has(r.b.hubId)));
  }, [branches, hubs, hubOwn4, shares, isAll, hubSet]);

  // Volume availability (hourly) for the filtered hub(s), week 1.
  const availW1 = useMemo(() => {
    const byHour = new Array<number>(24).fill(0);
    const opts = { overrides: overrideMap, events, loadCapacity: capByKey };
    for (const h of hubSet) for (const d of weeks4[0].dates) {
      const a = hourlyAvailability(customers, lhRows, h, d, opts);
      for (let i = 0; i < 24; i++) byHour[i] += a.byHour[i].total;
    }
    return byHour.map((v) => Math.round(v));
  }, [hubSet, weeks4, customers, lhRows, overrideMap, events, capByKey]);
  const peakHour = availW1.indexOf(Math.max(...availW1));

  // LH loads inbound by hub across the 4 weeks.
  const lhByHub = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => ({ h, loads: allDates.reduce((s, d) => s + lhLoadsInOnDay(lhRows, new Set([h.id]), d), 0) }))
        .filter((x) => x.loads > 0)
        .sort((a, b) => b.loads - a.loads),
    [hubs, hubSet, allDates, lhRows]
  );
  const lhTotal = lhByHub.reduce((s, x) => s + x.loads, 0);

  // Per-hub capacity highlight (peak day own vs daily capacity).
  const hubRows = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => {
          const total = hubOwn4.get(h.id) ?? 0;
          let peak = 0;
          for (const d of allDates) peak = Math.max(peak, plan.hubDayOwn.get(`${h.id}|${d}`) ?? 0);
          const util = h.capacity && h.capacity > 0 ? (peak / h.capacity) * 100 : null;
          return { h, total, peak, cap: h.capacity ?? null, util };
        })
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    [hubs, hubSet, hubOwn4, allDates, plan.hubDayOwn]
  );

  // MHE capacity by package type (filtered hubs): peak-day per type vs summed MHE caps.
  const mhe = useMemo(() => {
    const sel = hubs.filter((h) => hubSet.has(h.id));
    const sumCap = (pick: (h: Hub) => number | null | undefined): number | null => {
      const vals = sel.map(pick).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
    };
    const cap = { cartons: sumCap((h) => h.mheCartons), polybags: sumCap((h) => h.mhePolybag), irregular: sumCap((h) => h.mheIrregular) };
    const peak = { cartons: 0, polybags: 0, irregular: 0 };
    for (const sp of plan.dayPkg.values()) {
      peak.cartons = Math.max(peak.cartons, sp.cartons);
      peak.polybags = Math.max(peak.polybags, sp.polybags);
      peak.irregular = Math.max(peak.irregular, sp.irregular);
    }
    return { cap, peak };
  }, [hubs, hubSet, plan.dayPkg]);

  // Top customers (filtered) + their primary hub.
  const topCustomers = useMemo(() => {
    const primaryHub = (cid: string) => {
      let best = "";
      let bv = -1;
      for (const h of hubSet) {
        const v = plan.custHub.get(`${cid}|${h}`) ?? 0;
        if (v > bv) ((bv = v), (best = h));
      }
      return hubs.find((h) => h.id === best)?.code || hubs.find((h) => h.id === best)?.name || "—";
    };
    return [...plan.custTotal.entries()]
      .map(([id, value]) => ({ id, name: plan.custName.get(id) ?? id, value, hub: primaryHub(id) }))
      .sort((a, b) => b.value - a.value);
  }, [plan.custTotal, plan.custName, plan.custHub, hubSet, hubs]);

  // ── Drift (week 1, the committed week) ──
  const lockedW1 = useMemo(() => {
    if (!lock) return null;
    const per = lockedHubWeekTotals(lockCells);
    let v = 0;
    for (const h of hubSet) v += per.get(h) ?? 0;
    return v;
  }, [lock, lockCells, hubSet]);
  const liveW1 = plan.weekTot[0];
  const driftPct = lockedW1 && lockedW1 > 0 ? ((liveW1 - lockedW1) / lockedW1) * 100 : null;

  const lockedWeeks = useMemo(() => new Set(locks.map((l) => l.weekStart)), [locks]);
  const weekOptions = useMemo(() => {
    const base = startOfWeekSat(today());
    return Array.from({ length: WEEK_OPTIONS }, (_, i) => addDays(base, i * 7));
  }, []);

  async function doLock() {
    if (!accountId || !isAdmin) return;
    setBusy(true);
    setError(null);
    try {
      const cells = buildLockCells(customers, hubs, weeks4[0].dates, overrideMap, events);
      await createLock(accountId, startWeek, cells, liveFlows as unknown as LockFlow[], {
        label,
        note,
        lockedBy: session?.user?.id ?? null,
      });
      setLabel("");
      setNote("");
      setLocks(await listActiveLocks(accountId));
      await refreshLock();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to lock the week."));
    } finally {
      setBusy(false);
    }
  }

  async function doUnlock() {
    if (!accountId || !isAdmin) return;
    if (!window.confirm("Unlock week 1? The frozen plan is kept for history but the week is no longer locked.")) return;
    setBusy(true);
    setError(null);
    try {
      await unlockWeek(accountId, startWeek);
      setLocks(await listActiveLocks(accountId));
      await refreshLock();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to unlock the week."));
    } finally {
      setBusy(false);
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }
  if (loading) return <Spinner />;

  const hubLabel = isAll ? "All hubs · network" : hubs.find((h) => h.id === hubId)?.name ?? "—";
  const pkgSegments = [
    { label: "Cartons", value: Math.round(plan.pkgAll.cartons), color: TYPE_COLORS.cartons },
    { label: "Polybags", value: Math.round(plan.pkgAll.polybags), color: TYPE_COLORS.polybags },
    { label: "Irregular", value: Math.round(plan.pkgAll.irregular), color: TYPE_COLORS.irregular },
    { label: "Unspecified", value: Math.round(plan.pkgAll.unspec), color: TYPE_COLORS.unspec },
  ].filter((s) => s.value > 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Forecast Lock & 4-Week Plan"
        subtitle="A hub manager's planning view — volume, mix, capacity and LH for the next 4 weeks — with an admin lock to freeze week 1 as a committed plan."
      >
        <span className="text-xs text-slate-400">{hubLabel}</span>
      </PageHeader>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-56">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All hubs (network)</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-60">
            <Field label="Start week (week 1 = lock target)">
              <Select value={startWeek} onChange={(e) => setStartWeek(e.target.value)}>
                {weekOptions.map((w) => (
                  <option key={w} value={w}>
                    {fmtDate(w)} – {fmtDate(addDays(w, 6))}
                    {lockedWeeks.has(w) ? "  🔒" : ""}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-32">
            <Field label="Top rows">
              <Select value={String(topN)} onChange={(e) => setTopN(Number(e.target.value))}>
                <option value="5">Top 5</option>
                <option value="8">Top 8</option>
                <option value="12">Top 12</option>
                <option value="999">All</option>
              </Select>
            </Field>
          </div>
          <p className="text-xs text-slate-400">
            4 weeks: {fmtDate(weeks4[0].weekStart)} → {fmtDate(weeks4[3].weekEnd)}
          </p>
        </div>
      </Card>

      {/* Lock bar */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          {lock ? (
            <>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">🔒 Week 1 locked</span>
                <p className="mt-1 text-xs text-slate-500">
                  {lock.label ? <b className="text-slate-700">{lock.label} · </b> : null}
                  locked {fmtDate(lock.lockedAt.slice(0, 10))} · {fmtDate(startWeek)}–{fmtDate(weeks4[0].weekEnd)}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" onClick={doUnlock} disabled={!isAdmin || busy}>Unlock</Button>
                <Button onClick={doLock} disabled={!isAdmin || busy}>{busy ? "Working…" : "Re-lock (refresh snapshot)"}</Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-44">
                <Field label="Label (optional)">
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Wk26 plan" />
                </Field>
              </div>
              <div className="w-72">
                <Field label="Note (optional)">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this plan is committed…" />
                </Field>
              </div>
              <Button onClick={doLock} disabled={!isAdmin || busy}>{busy ? "Locking…" : "🔒 Lock week 1"}</Button>
            </>
          )}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          ⓘ <b>Changing a locked plan:</b> don't edit the frozen numbers — adjust inputs (customers, calibration, events,
          overrides), watch the <b>drift</b> grow, then <b>Re-lock</b> to commit the new plan (the old snapshot is kept as
          history). <b>Unlock</b> abandons the commitment. {!isAdmin && <span className="text-amber-600">You have view-only access — only admins can lock/unlock.</span>}
        </p>
      </Card>

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label="4-week forecast" value={fmtN(total4wk)} sub={`${fmtDate(weeks4[0].weekStart)}–${fmtDate(weeks4[3].weekEnd)}`} />
        <Kpi label="Peak week" value={fmtN(plan.weekTot[peakWeekIdx])} sub={`W${peakWeekIdx + 1} · ${fmtDate(weeks4[peakWeekIdx].weekEnd)}`} />
        <Kpi label="Peak day" value={fmtN(peakDay.value)} sub={peakDay.date ? fmtDate(peakDay.date) : "—"} />
        <Kpi label="LH loads inbound" value={fmtN(lhTotal)} sub="next 4 weeks" />
        <Kpi
          label="Drift vs locked (W1)"
          value={driftPct == null ? "—" : `${driftPct > 0 ? "+" : ""}${driftPct.toFixed(1)}%`}
          sub={lock ? "live − locked" : "week 1 not locked"}
          tone={driftPct == null ? undefined : Math.abs(driftPct) > 10 ? "bad" : Math.abs(driftPct) > 3 ? "warn" : "good"}
        />
      </div>

      {/* Volume by week + package mix */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="mb-1 text-sm font-medium text-slate-700">Forecast volume by week</div>
          <p className="mb-2 text-[11px] text-slate-400">Week 1 is the lock target (highlighted). {hubLabel}.</p>
          <BarsChart
            highlightIndex={0}
            data={weeks4.map((w, i) => ({ label: `W${i + 1}`, value: Math.round(plan.weekTot[i]), color: WEEK_COLORS[i], sub: fmtDate(w.weekEnd) }))}
          />
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Package-type mix · 4 weeks</div>
          <p className="mb-2 text-[11px] text-slate-400">Share of total volume by handling type.</p>
          {pkgSegments.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No package mix set on lanes.</p>
          ) : (
            <div className="flex items-center gap-4">
              <Donut segments={pkgSegments} />
              <div className="space-y-1 text-xs">
                {pkgSegments.map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    <span className="text-slate-600">{s.label}</span>
                    <span className="ml-auto tabular-nums font-medium text-slate-700">{fmtN(s.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Package mix by week (stacked) */}
      <Card>
        <div className="mb-1 text-sm font-medium text-slate-700">Package-type mix by week</div>
        <div className="mb-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
          {(["cartons", "polybags", "irregular", "unspec"] as const).map((k) => (
            <span key={k} className="flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm" style={{ backgroundColor: TYPE_COLORS[k] }} />
              {k === "unspec" ? "Unspecified" : k.charAt(0).toUpperCase() + k.slice(1)}
            </span>
          ))}
        </div>
        <StackedBars
          series={[
            { label: "Cartons", color: TYPE_COLORS.cartons },
            { label: "Polybags", color: TYPE_COLORS.polybags },
            { label: "Irregular", color: TYPE_COLORS.irregular },
            { label: "Unspecified", color: TYPE_COLORS.unspec },
          ]}
          groups={weeks4.map((w, i) => ({
            label: `W${i + 1}`,
            sub: fmtDate(w.weekEnd),
            values: [
              Math.round(plan.pkgWeek[i].cartons),
              Math.round(plan.pkgWeek[i].polybags),
              Math.round(plan.pkgWeek[i].irregular),
              Math.round(plan.pkgWeek[i].unspec),
            ],
          }))}
        />
      </Card>

      {/* By hub (capacity) + Top branches */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Volume by hub · 4 weeks {isAll ? "" : "(selected)"}</div>
          <p className="mb-3 text-[11px] text-slate-400">Bar = 4-week volume. Right = peak-day vs daily capacity (red &gt;100%, amber &gt;85%).</p>
          <HBarList
            items={hubRows.slice(0, topN).map((r, i) => ({
              label: r.h.code || r.h.name,
              value: Math.round(r.total),
              color: HUB_PALETTE[i % HUB_PALETTE.length],
              tone: r.util == null ? undefined : r.util > 100 ? "bad" : r.util > 85 ? "warn" : "ok",
              right: r.util == null ? "no cap" : `${Math.round(r.util)}%`,
            }))}
          />
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Top branches · 4 weeks</div>
          <p className="mb-3 text-[11px] text-slate-400">Network-flow volume delivered to each branch.</p>
          {branchTotals.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No branch volume.</p>
          ) : (
            <HBarList
              items={branchTotals
                .sort((a, b) => b.value - a.value)
                .slice(0, topN)
                .map((r) => ({ label: r.b.name, value: Math.round(r.value), color: "#6366f1" }))}
            />
          )}
        </Card>
      </div>

      {/* Top customers + customer×hub matrix */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Top customers · 4 weeks</div>
          <p className="mb-3 text-[11px] text-slate-400">Largest shippers by forecast volume (primary hub shown).</p>
          <HBarList
            items={topCustomers.slice(0, topN).map((c) => ({ label: c.name, value: Math.round(c.value), color: "#0ea5e9", right: c.hub }))}
          />
        </Card>
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">Customer forecast by hub · 4 weeks</div>
            <p className="mt-0.5 text-[11px] text-slate-400">Top customers × hub (only columns with volume).</p>
          </div>
          <CustomerHubMatrix
            customers={topCustomers.slice(0, topN)}
            hubs={hubs.filter((h) => hubSet.has(h.id))}
            value={(cid, hid) => plan.custHub.get(`${cid}|${hid}`) ?? 0}
          />
        </Card>
      </div>

      {/* LH inbound by hub + MHE capacity */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">LH loads inbound by hub · 4 weeks</div>
          <p className="mb-3 text-[11px] text-slate-400">Scheduled linehaul loads arriving at each hub.</p>
          {lhByHub.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No LH schedule for these hubs.</p>
          ) : (
            <HBarList items={lhByHub.slice(0, topN).map((x) => ({ label: x.h.code || x.h.name, value: x.loads, color: "#10b981" }))} unit=" loads" />
          )}
        </Card>
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">MHE capacity by package type · peak day</div>
            <p className="mt-0.5 text-[11px] text-slate-400">Busiest single day in the window vs daily MHE sortation capacity.</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Type</th>
                <th className="px-3 py-2 text-right font-medium">Cap/day</th>
                <th className="px-3 py-2 text-right font-medium">Peak day</th>
                <th className="px-5 py-2 text-right font-medium">Util</th>
              </tr>
            </thead>
            <tbody>
              {([
                { label: "Cartons", color: TYPE_COLORS.cartons, cap: mhe.cap.cartons, peak: mhe.peak.cartons },
                { label: "Polybags", color: TYPE_COLORS.polybags, cap: mhe.cap.polybags, peak: mhe.peak.polybags },
                { label: "Irregular", color: TYPE_COLORS.irregular, cap: mhe.cap.irregular, peak: mhe.peak.irregular },
              ] as const).map((r) => {
                const util = r.cap && r.cap > 0 ? (r.peak / r.cap) * 100 : null;
                const cls = r.cap == null ? "text-slate-400" : util != null && util > 100 ? "text-red-600" : util != null && util > 85 ? "text-amber-600" : "text-emerald-600";
                return (
                  <tr key={r.label} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: r.color }} />
                      {r.label}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.cap == null ? "—" : fmtN(r.cap)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(Math.round(r.peak))}</td>
                    <td className={`px-5 py-2 text-right tabular-nums font-medium ${cls}`}>{util == null ? "—" : `${Math.round(util)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Volume availability */}
      <Card>
        <div className="mb-1 text-sm font-medium text-slate-700">Volume availability · week 1 hourly profile</div>
        <p className="mb-2 text-[11px] text-slate-400">
          When volume physically lands and is available to sort across the day (scheduled arrivals + overflow), {hubLabel}.
          Peak hour: <b className="text-slate-600">{String(peakHour).padStart(2, "0")}:00</b>.
        </p>
        <HourBars byHour={availW1} />
      </Card>

      {lock?.note && (
        <Card>
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Lock note</p>
          <p className="mt-1 text-sm text-slate-700">{lock.note}</p>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const c = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-slate-900";
  const dot = tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : tone === "good" ? "bg-emerald-500" : "";
  return (
    <Card>
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${c}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function CustomerHubMatrix({
  customers,
  hubs,
  value,
}: {
  customers: Array<{ id: string; name: string }>;
  hubs: Hub[];
  value: (custId: string, hubId: string) => number;
}) {
  // Only hubs that have any volume across the shown customers.
  const cols = hubs.filter((h) => customers.some((c) => value(c.id, h.id) > 0));
  const max = Math.max(1, ...customers.flatMap((c) => cols.map((h) => value(c.id, h.id))));
  if (cols.length === 0) return <p className="px-5 py-8 text-center text-sm text-slate-400">No volume.</p>;
  return (
    <div className="max-h-[40vh] overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
            <th className="px-3 py-2 text-left font-medium">Customer</th>
            {cols.map((h) => (
              <th key={h.id} className="px-2 py-2 text-right font-medium">{h.code || h.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {customers.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-1.5 font-medium text-slate-700">{c.name}</td>
              {cols.map((h) => {
                const v = value(c.id, h.id);
                const alpha = v > 0 ? 0.08 + 0.5 * (v / max) : 0;
                return (
                  <td key={h.id} className="px-2 py-1.5 text-right tabular-nums text-slate-700" style={{ backgroundColor: v > 0 ? `rgba(15,118,110,${alpha})` : undefined }}>
                    {v > 0 ? fmtN(v) : "·"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
